import { useState, useRef } from "react";
import { Card, Stack, Title, IconButton, Select, Option } from "@introist/react-foundation/v2";
import { HexColorPickerPopover } from "components/editors/v4/HtmlEditor/Toolbar/components/HexColorPickerPopover";
import { ImageUploadArea } from "components/molecules/ImageUploadArea/ImageUploadArea";
import { FontPickerWrapper } from "./FontPickerWrapper";
import styled from "styled-components";

const ColorCircleButton = styled.button<{ $color?: string }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.$color || "transparent"};
  border: 2px solid var(--border-color, #e0e0e0);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--palette-primary-default);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  ${props =>
    !props.$color &&
    `
    background-image:
      linear-gradient(45deg, #ccc 25%, transparent 25%),
      linear-gradient(-45deg, #ccc 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #ccc 75%),
      linear-gradient(-45deg, transparent 75%, #ccc 75%);
    background-size: 8px 8px;
    background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
  `}
`;

const SmallImageUploadArea = styled.div`
  width: 48px;
  height: 48px;

  & > div {
    width: 48px;
    height: 48px;

    img {
      width: 48px;
      height: 48px;
      object-fit: contain;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

type BackgroundImageMode = "full" | "banner";

interface FormBranding {
  backgroundColor?: string;
  primaryColor?: string;
  logoUrl?: string;
  backgroundImageUrl?: string;
  backgroundImageMode?: BackgroundImageMode;
  fontFamily?: string | null;
}

export interface BrandingSettingsProps {
  branding: FormBranding;
  onChange: (branding: FormBranding) => void;
}

export const BrandingSettings = ({ branding, onChange }: BrandingSettingsProps) => {
  const [bgColorPickerOpen, setBgColorPickerOpen] = useState(false);
  const [primaryColorPickerOpen, setPrimaryColorPickerOpen] = useState(false);
  const bgColorButtonRef = useRef<HTMLButtonElement>(null);
  const primaryColorButtonRef = useRef<HTMLButtonElement>(null);

  // Update branding with changes
  const updateBranding = (changes: Partial<FormBranding>) => {
    onChange({ ...branding, ...changes });
  };

  return (
    <>
      {/* Branding Settings */}
      <Card style={{ padding: "var(--spacing-medium)" }}>
        <Stack vertical gap="medium">
          <Title variant="bold">Branding</Title>

          {/* Group 1: Images */}
          <Stack vertical gap="medium">
            {/* Logo */}
            <Stack gap="small" alignItems="center">
              <Title style={{ fontSize: "14px", flex: 1 }}>Logo</Title>
              <Stack gap="small" alignItems="center">
                <SmallImageUploadArea>
                  <ImageUploadArea
                    imageUrl={branding.logoUrl}
                    onImageUploaded={url => updateBranding({ logoUrl: url })}
                    alt="Form logo"
                    maxSize={1 * 1024 * 1024}
                    acceptedFileTypes={[".png", ".jpg", ".jpeg", ".svg"]}
                  />
                </SmallImageUploadArea>
                <IconButton
                  icon="crossSmall"
                  variant="blended"
                  size="small"
                  onClick={() => updateBranding({ logoUrl: undefined })}
                  aria-label="Clear logo"
                  style={{ visibility: branding.logoUrl ? "visible" : "hidden" }}
                />
              </Stack>
            </Stack>

            {/* Background Image */}
            <Stack gap="small" alignItems="center">
              <Title style={{ fontSize: "14px", flex: 1 }}>Background Image</Title>
              <Stack gap="small" alignItems="center">
                {branding.backgroundImageUrl && (
                  <Select
                    element="button"
                    variant="outlined"
                    value={branding.backgroundImageMode || "full"}
                    onSelect={(option: Option) =>
                      updateBranding({ backgroundImageMode: option.key as "full" | "banner" })
                    }
                    options={[
                      { key: "full", title: "Full" },
                      { key: "banner", title: "Banner" }
                    ]}
                    size="small"
                  />
                )}
                <SmallImageUploadArea>
                  <ImageUploadArea
                    imageUrl={branding.backgroundImageUrl}
                    onImageUploaded={url => updateBranding({ backgroundImageUrl: url })}
                    alt="Background image"
                    maxSize={5 * 1024 * 1024}
                    acceptedFileTypes={[".png", ".jpg", ".jpeg"]}
                  />
                </SmallImageUploadArea>
                <IconButton
                  icon="crossSmall"
                  variant="blended"
                  size="small"
                  onClick={() => updateBranding({ backgroundImageUrl: undefined })}
                  aria-label="Clear background image"
                  style={{ visibility: branding.backgroundImageUrl ? "visible" : "hidden" }}
                />
              </Stack>
            </Stack>
          </Stack>

          {/* Spacer */}
          <div style={{ height: "var(--spacing-medium)" }} />

          {/* Group 2: Colors */}
          <Stack vertical gap="medium">
            {/* Background Color */}
            <Stack gap="small" alignItems="center">
              <Title style={{ fontSize: "14px", flex: 1 }}>Background Color</Title>
              <Stack gap="small" alignItems="center">
                <ColorCircleButton
                  ref={bgColorButtonRef}
                  $color={branding.backgroundColor}
                  onClick={() => setBgColorPickerOpen(!bgColorPickerOpen)}
                  aria-label="Select background color"
                />
                <IconButton
                  icon="crossSmall"
                  variant="blended"
                  size="small"
                  onClick={() => updateBranding({ backgroundColor: undefined })}
                  aria-label="Clear background color"
                  style={{ visibility: branding.backgroundColor ? "visible" : "hidden" }}
                />
              </Stack>
            </Stack>

            {/* Primary Color */}
            <Stack gap="small" alignItems="center">
              <Title style={{ fontSize: "14px", flex: 1 }}>Primary Color</Title>
              <Stack gap="small" alignItems="center">
                <ColorCircleButton
                  ref={primaryColorButtonRef}
                  $color={branding.primaryColor}
                  onClick={() => setPrimaryColorPickerOpen(!primaryColorPickerOpen)}
                  aria-label="Select primary color"
                />
                <IconButton
                  icon="crossSmall"
                  variant="blended"
                  size="small"
                  onClick={() => updateBranding({ primaryColor: undefined })}
                  aria-label="Clear primary color"
                  style={{ visibility: branding.primaryColor ? "visible" : "hidden" }}
                />
              </Stack>
            </Stack>
          </Stack>

          {/* Spacer */}
          <div style={{ height: "var(--spacing-medium)" }} />

          {/* Group 3: Font */}
          <Stack gap="small" alignItems="center">
            <Title style={{ fontSize: "14px", flex: 1 }}>Font</Title>
            <FontPickerWrapper
              activeFontFamily={branding.fontFamily}
              onChange={fontFamily => updateBranding({ fontFamily })}
            />
          </Stack>
        </Stack>
      </Card>

      {/* Color Pickers */}
      <HexColorPickerPopover
        referenceElement={bgColorButtonRef.current}
        open={bgColorPickerOpen}
        onClose={() => setBgColorPickerOpen(false)}
        onSelect={color => updateBranding({ backgroundColor: color })}
        onClear={() => updateBranding({ backgroundColor: undefined })}
        color={branding.backgroundColor}
      />
      <HexColorPickerPopover
        referenceElement={primaryColorButtonRef.current}
        open={primaryColorPickerOpen}
        onClose={() => setPrimaryColorPickerOpen(false)}
        onSelect={color => updateBranding({ primaryColor: color })}
        onClear={() => updateBranding({ primaryColor: undefined })}
        color={branding.primaryColor}
      />
    </>
  );
};
